import type { ApiResponse } from '@vagusx/shared';
import { HttpError } from './errors';

export interface RequestOptions extends RequestInit {
  signal?: AbortSignal;
}

/**
 * API key authentication config.
 */
export interface ApiKeyAuth {
  apiKey: string;
  organizationId: string;
}

// Check if running in browser environment
const isBrowser = typeof globalThis !== 'undefined' && 'window' in globalThis;

interface BrowserLocation {
  pathname: string;
  search: string;
  hash: string;
  href: string;
}

/**
 * Where a 401 sends the browser: /login, carrying the page the user was on so
 * sign-in returns them there — the same `redirect` param the web app's route
 * guard sets when it catches a signed-out session itself.
 */
export function loginRedirectUrl(
  location: Pick<BrowserLocation, 'pathname' | 'search' | 'hash'>
): string {
  const from = `${location.pathname}${location.search}${location.hash}`;
  return `/login?redirect=${encodeURIComponent(from)}`;
}

/**
 * Base HTTP client for API calls with session-based or API key authentication.
 * Supports:
 * - Browser: cookies (automatic)
 * - Server: session token (Bearer header)
 * - Server: API key (X-API-Key + X-Organization-Id headers)
 *
 * Handles 401 errors by redirecting to login in browser.
 */
export class AuthBaseClient {
  protected baseUrl: string;
  private sessionToken: string | null = null;
  private apiKeyAuth: ApiKeyAuth | null = null;

  constructor(baseUrl: string) {
    if (!baseUrl && !isBrowser) {
      throw new Error('baseUrl is required for server-side usage');
    }
    this.baseUrl = baseUrl;
  }

  /**
   * Set session token for server-side usage.
   * Browser clients use cookies automatically.
   */
  setSessionToken(token: string): void {
    this.sessionToken = token;
    this.apiKeyAuth = null; // Clear API key auth if session token is set
  }

  /**
   * Get current session token.
   */
  getSessionToken(): string | null {
    return this.sessionToken;
  }

  /**
   * Set API key authentication for server-side usage.
   * Requires both API key and organization ID.
   */
  setApiKeyAuth(apiKey: string, organizationId: string): void {
    this.apiKeyAuth = { apiKey, organizationId };
    this.sessionToken = null; // Clear session token if API key is set
  }

  /**
   * Get current API key auth config.
   */
  getApiKeyAuth(): ApiKeyAuth | null {
    return this.apiKeyAuth;
  }

  /**
   * Handle API response: check success, redirect on 401, throw on error.
   */
  private handleResponse<T>(response: Response, body: ApiResponse<T>): T {
    if (!body.success) {
      if (response.status === 401 && isBrowser) {
        const win = globalThis as unknown as { location?: BrowserLocation };
        if (win.location && win.location.pathname !== '/login') {
          win.location.href = loginRedirectUrl(win.location);
          return new Promise(() => {}) as T; // Never resolves
        }
      }
      throw HttpError.fromResponse(body as ApiResponse<never>);
    }
    return body.data!;
  }

  /**
   * Build auth headers for requests.
   */
  private buildAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.apiKeyAuth) {
      headers['X-API-Key'] = this.apiKeyAuth.apiKey;
      headers['X-Organization-Id'] = this.apiKeyAuth.organizationId;
    } else if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    return headers;
  }

  /**
   * Make HTTP request with session or API key handling.
   */
  protected async request<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.buildAuthHeaders(),
      ...((options.headers as Record<string, string>) ?? {}),
    };

    const response = await fetch(`${this.baseUrl}${url}`, {
      ...options,
      headers,
      credentials: 'include',
      signal: options.signal,
    });

    const body = (await response.json()) as ApiResponse<T>;
    return this.handleResponse(response, body);
  }

  /**
   * GET request.
   */
  protected async get<T>(url: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, { method: 'GET', signal });
  }

  /**
   * POST request.
   */
  protected async post<T>(url: string, data?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  }

  /**
   * PATCH request.
   */
  protected async patch<T>(url: string, data?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      signal,
    });
  }

  /**
   * PUT request.
   */
  protected async put<T>(url: string, data?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal,
    });
  }

  /**
   * DELETE request.
   */
  protected async delete<T>(url: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', signal });
  }

  /**
   * GET a binary response as a Blob (file downloads). On error the API still
   * returns the JSON envelope, so we parse it for a structured HttpError.
   */
  protected async getBlob(url: string, signal?: AbortSignal): Promise<Blob> {
    return this.fetchBlob(url, { method: 'GET', headers: this.buildAuthHeaders(), signal });
  }

  /**
   * POST a JSON body and read a binary response as a Blob (synthesized audio).
   * Errors come back as the JSON envelope, exactly as for `getBlob`.
   */
  protected async postBlob(url: string, data: unknown, signal?: AbortSignal): Promise<Blob> {
    return this.fetchBlob(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.buildAuthHeaders() },
      body: JSON.stringify(data),
      signal,
    });
  }

  private async fetchBlob(url: string, init: RequestInit): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}${url}`, { ...init, credentials: 'include' });

    if (!response.ok) {
      if (response.status === 401 && isBrowser) {
        const win = globalThis as unknown as { location?: BrowserLocation };
        if (win.location && win.location.pathname !== '/login') {
          win.location.href = loginRedirectUrl(win.location);
          return new Promise(() => {}); // Never resolves
        }
      }
      const body = (await response.json().catch(() => null)) as ApiResponse<never> | null;
      if (body) throw HttpError.fromResponse(body);
      throw new HttpError(response.status, 'SYSTEM', `Request failed (${response.status})`);
    }

    return response.blob();
  }

  /**
   * Upload FormData (multipart). Omits Content-Type so the browser sets the boundary.
   */
  protected async uploadFormData<T>(
    url: string,
    formData: FormData,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'POST',
      headers: this.buildAuthHeaders(),
      body: formData,
      credentials: 'include',
      signal,
    });

    const body = (await response.json()) as ApiResponse<T>;
    return this.handleResponse(response, body);
  }
}
